/**
 * Bits shared by the adult and kid chore views. Presentation only — no fetching.
 * Per-member colour always comes from `family_members.color` in D1, never a constant.
 *
 * There is NO day/week maths in here. "Already done today" is a household rule with
 * a household timezone, and the Worker owns it: the views ask for
 * `/chores/completions?scope=current` and render what comes back. A browser on UTC
 * and a house on America/New_York used to disagree about what day it was, so the
 * kid saw an "I did it!" button that answered 409.
 */
import { useEffect, type ReactNode } from 'react';
import type { Chore, ChoreCompletion, MemberSummary } from '../../shared/types';

export const cx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ');

export const btnPrimary =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-sage px-4 text-sm font-medium text-white transition-colors hover:bg-sage/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const btnSecondary =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const btnQuiet =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-3 text-sm font-medium text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const fieldLabel = 'block text-sm font-medium text-ink';
export const fieldInput =
  'mt-1 block w-full min-h-[44px] rounded-xl border border-border bg-surface px-3 py-2 text-base text-ink placeholder:text-ink-faint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage';

// ---------------------------------------------------------------- dates

/**
 * A timestamp, in the reader's own device format. Deliberately makes no claim
 * about which household day it fell on — that is the Worker's call, not the
 * browser's.
 */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const CADENCE_LABEL: Record<Chore['cadence'], string> = {
  daily: 'Every day',
  weekly: 'Once a week',
  once: 'One time',
};

export interface ChoreState {
  /** The completion covering the current period, if there is one. */
  completion: ChoreCompletion | null;
  status: 'todo' | 'pending' | 'approved';
}

/**
 * `completions` MUST be the server's current-window set (`?scope=current`): the
 * Worker has already decided, in the household timezone, which completions still
 * count for each chore's cadence. All that is left here is matching them up.
 */
export function choreState(
  chore: Chore,
  completions: ChoreCompletion[],
  memberId: number | null
): ChoreState {
  const match = completions
    .filter(
      (cc) => cc.chore_id === chore.id && (memberId == null || cc.member_id === memberId)
    )
    .sort((a, b) => b.completed_at.localeCompare(a.completed_at))[0];

  if (!match) return { completion: null, status: 'todo' };
  return { completion: match, status: match.approved_by ? 'approved' : 'pending' };
}

// ---------------------------------------------------------------- members

export const memberById = (members: MemberSummary[], id: number | null) =>
  id == null ? null : (members.find((m) => m.id === id) ?? null);

export const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

export function Avatar({
  member,
  size = 'md',
}: {
  member: MemberSummary | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dims =
    size === 'lg' ? 'h-14 w-14 text-xl' : size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-grid shrink-0 place-items-center rounded-full font-semibold text-white',
        dims
      )}
      style={{ backgroundColor: member?.color ?? 'var(--color-ink-faint)' }}
    >
      {member ? initials(member.name) : '?'}
    </span>
  );
}

/** Name chip in the member's own colour. Colour is never the only signal — the name is there too. */
export function MemberChip({ member }: { member: MemberSummary | null }) {
  if (!member) return <span className="text-sm text-ink-faint">Unassigned</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-ink">
      <span
        aria-hidden="true"
        className="h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: member.color }}
      />
      {member.name}
    </span>
  );
}

// ---------------------------------------------------------------- shells

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cx('rounded-card border border-border bg-surface p-5', className)}>
      {children}
    </section>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="font-display text-lg font-semibold text-ink">{children}</h2>
      {action}
    </div>
  );
}

export function LoadingState({ label = 'Loading chores…' }: { label?: string }) {
  return (
    <div role="status" className="space-y-3">
      <p className="text-sm text-ink-faint">{label}</p>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-20 animate-pulse rounded-card border border-border bg-surface-sunk"
        />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card className="border-over/40 bg-clay-soft">
      <p className="font-medium text-ink">Something went wrong</p>
      <p className="mt-1 text-sm text-ink-soft">{message}</p>
      <button type="button" onClick={onRetry} className={cx(btnSecondary, 'mt-4')}>
        Try again
      </button>
    </Card>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <Card className="border-dashed text-center">
      <p className="font-display text-lg font-semibold text-ink">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-ink-soft">{body}</p>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </Card>
  );
}

/** A small centred dialog. Escape closes it; the backdrop click does too. */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = `dlg-${title.replace(/\W+/g, '-').toLowerCase()}`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-md rounded-t-card border border-border bg-surface p-5 shadow-xl sm:rounded-card"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="font-display text-lg font-semibold text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-mt-1 -mr-1 grid h-11 w-11 place-items-center rounded-xl text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              ✕
            </span>
            <span className="sr-only">Close</span>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Inline, dismissible feedback for an action that just happened (or failed). */
export function Banner({ tone, message }: { tone: 'ok' | 'error'; message: string }) {
  return (
    <p
      role="status"
      className={cx(
        'rounded-xl border px-4 py-3 text-sm',
        tone === 'ok'
          ? 'border-sage/30 bg-sage-soft text-sage'
          : 'border-over/40 bg-clay-soft text-clay'
      )}
    >
      {tone === 'ok' ? '✓ ' : '! '}
      {message}
    </p>
  );
}
